import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronDown, Plus, Settings, ArrowRight, Loader2, FlaskConical, Pencil, Save, Trash2, BookOpen } from 'lucide-react';
import { Button, InputField, TabSwitcher } from 'equal-ds-ui';
import { analyticsOnboardingAPI } from '../../../services/analytics-onboarding-api.service';
import { JOURNEY_TEMPLATES, WORKFLOW_IDS } from '../../../types/analytics-onboarding';
import type { ClientAnalyticsConfig, ConfiguredJourneyInfo, ConfiguredJourney } from '../../../types/analytics-onboarding';
import AttributeSelector from './AttributeSelector';

type ActiveType = 'analytics-json' | 'analytics-excel' | 'pdf-json' | 'pdf-excel';

const TYPE_LABEL: Record<ActiveType, string> = {
  'analytics-json': 'JSON Analytics',
  'analytics-excel': 'Excel Analytics',
  'pdf-json': 'PDF JSON Analytics',
  'pdf-excel': 'PDF Excel Analytics',
};

// Each journey maps to exactly ONE config type based on its workflow + template
const getJourneyConfigType = (j: ConfiguredJourneyInfo): ActiveType => {
  const isPdf = j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.PDF;
  const isExcel = !!(j.journey_workflow_config?.workflow_configuration as any)?.report_configuration?.excel_template;
  if (isPdf) return isExcel ? 'pdf-excel' : 'pdf-json';
  return isExcel ? 'analytics-excel' : 'analytics-json';
};


interface ManageJourneysViewProps {
  clientConfig: ClientAnalyticsConfig;
  onBack: () => void;
  onTest: () => void;
  onError: (err: unknown, context?: Record<string, any>) => void;
  onExploreAttributes?: (journeyId: string) => void;
}

const toJourneyId = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const TEMPLATE_TABS = [
  { id: 'EXCEL_STANDARD', label: 'Excel Standard' },
  { id: 'JSON_STANDARD', label: 'JSON Standard' },
  { id: 'DYNAMIC_ATTRIBUTES', label: 'Dynamic Attributes' },
];


/* ── Journey config visual view ── */
const JourneyConfigView: React.FC<{ config: any }> = ({ config }) => {
  if (!config) return <div style={{ color: '#94a3b8', fontSize: 13 }}>No configuration</div>;

  const analyticsConfig = config.analytics_configuration || {};
  const attrConfig = analyticsConfig.analytics_attributes_configuration;

  // Collect all attributes flat
  const collectAttrs = (obj: any): any[] => {
    if (!obj || typeof obj !== 'object') return [];
    const attrs: any[] = [];
    for (const [key, val] of Object.entries(obj)) {
      if (key === 'analytics_attributes' && Array.isArray(val)) attrs.push(...(val as any[]));
      else if (typeof val === 'object' && val !== null) attrs.push(...collectAttrs(val));
    }
    return attrs;
  };

  const allAttrs = attrConfig ? collectAttrs(attrConfig) : [];
  const customAttrs = allAttrs.filter(a => a.type === 'custom');

  return (
    <div>
      {/* Selected attributes */}
      {attrConfig && Object.keys(attrConfig).length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Selected Attributes</b></div>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, backgroundColor: '#eff6ff', color: '#1d4ed8' }}>{allAttrs.length}</span>
              {customAttrs.length > 0 && (
                <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, backgroundColor: '#f5f3ff', color: '#7c3aed' }}>{customAttrs.length} custom</span>
              )}
            </div>
          </div>

          <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, maxHeight: 350, overflowY: 'auto' }}>
            <div>
              {Object.entries(attrConfig).map(([sectionKey, sectionData]: [string, any]) => {
                if (!sectionData || typeof sectionData !== 'object') return null;
                const sectionName = sectionKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()).trim();
                return (
                  <React.Fragment key={sectionKey}>
                    {/* Section header */}
                    <div style={{ padding: '8px 16px', backgroundColor: '#f0f9ff', borderBottom: '1px solid #e0f2fe' }}>
                      <span style={{ fontSize: 12, color: '#0369a1' }}><b>{sectionName}</b></span>
                    </div>
                    {Object.entries(sectionData).map(([catKey, catData]: [string, any]) => {
                      if (!catData || typeof catData !== 'object') return null;
                      const catName = catKey.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
                      return (
                        <React.Fragment key={catKey}>
                          {/* Category header */}
                          <div style={{ padding: '7px 16px 7px 28px', backgroundColor: '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
                            <span style={{ fontSize: 12, color: '#475569' }}><b>{catName}</b></span>
                          </div>
                          {Object.entries(catData).map(([tfKey, tfData]: [string, any]) => {
                            const attrs = tfData?.analytics_attributes;
                            if (!attrs || !Array.isArray(attrs) || attrs.length === 0) return null;
                            return (
                              <React.Fragment key={tfKey}>
                                {/* Timeframe label */}
                                <div style={{ padding: '5px 16px 5px 40px', borderBottom: '1px solid #f8fafc' }}>
                                  <span style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{tfKey} ({attrs.length})</span>
                                </div>
                                {/* Attribute rows */}
                                {attrs.map((attr: any, ai: number) => (
                                  <div key={`${attr.name}-${ai}`} style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 16px 8px 52px',
                                    borderBottom: '1px solid #f8fafc',
                                    backgroundColor: attr.type === 'custom' ? '#fefbff' : 'transparent',
                                  }}>
                                    <input type="checkbox" checked disabled style={{ width: 15, height: 15, accentColor: attr.type === 'custom' ? '#7c3aed' : '#2563eb', flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: 13, color: '#0f172a' }}>{attr.name}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}>
                                      {attr.type === 'custom' ? (
                                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, backgroundColor: '#f5f3ff', color: '#7c3aed' }}>custom</span>
                                      ) : (
                                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, backgroundColor: '#f1f5f9', color: '#64748b' }}>{attr.type || 'value'}</span>
                                      )}
                                      {attr.data_type && (
                                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, backgroundColor: '#dbeafe', color: '#1e40af' }}>{attr.data_type}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {allAttrs.length === 0 && (
        <div style={{ padding: '12px 16px', borderRadius: 10, backgroundColor: '#eff6ff', border: '1px solid #dbeafe', fontSize: 13, color: '#1d4ed8', lineHeight: 1.5 }}>
          <b>All attributes are configured.</b> Selecting specific attributes while editing will override this and keep only the selected ones.
        </div>
      )}
    </div>
  );
};

export const ManageJourneysView: React.FC<ManageJourneysViewProps> = ({ clientConfig, onBack, onTest, onError, onExploreAttributes }) => {
  const [existingJourneys, setExistingJourneys] = useState<ConfiguredJourneyInfo[]>([]);
  const [activeJourneys, setActiveJourneys] = useState<Record<string, string | null>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedJourney, setExpandedJourney] = useState<number | null>(null);
  const [editingJourney, setEditingJourney] = useState<number | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editAttrConfig, setEditAttrConfig] = useState<any>(null);
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [enablingKey, setEnablingKey] = useState<string | null>(null); // journeyId+type

  // Add new journey state
  const [showAddJourney, setShowAddJourney] = useState(false);
  const [newJourneyId, setNewJourneyId] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('EXCEL_STANDARD');
  const [inputType, setInputType] = useState<'consent' | 'pdf'>('consent');
  const [newJourneyConfig, setNewJourneyConfig] = useState<ConfiguredJourney | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [copyFromJourney, setCopyFromJourney] = useState('');

  const consentJourneys = existingJourneys.filter(j => j.journey_workflow_config?.workflow_id === WORKFLOW_IDS.CONSENT);

  const visibleJourneys = existingJourneys;

  const loadData = useCallback(async () => {
    if (!clientConfig.exchange_application_id) { setIsLoading(false); return; }
    try {
      const [journeyData, activeData] = await Promise.all([
        analyticsOnboardingAPI.getConfiguredJourneys(clientConfig.exchange_application_id),
        analyticsOnboardingAPI.getActiveJourneys(),
      ]);
      setExistingJourneys(journeyData?.configured_journeys || []);
      setActiveJourneys(activeData || {});
    } catch (err) { onError(err, { component: 'ManageJourneysView' }); }
    finally { setIsLoading(false); }
  }, [clientConfig.exchange_application_id]);

  useEffect(() => { loadData(); }, [loadData]);

  // Initialize new journey from template + input type
  useEffect(() => {
    const templateKey = inputType === 'pdf' ? `${selectedTemplate}_PDF` : selectedTemplate;
    const template = JOURNEY_TEMPLATES[templateKey as keyof typeof JOURNEY_TEMPLATES];
    if (template) {
      const j = { ...template, journey_id: newJourneyId || toJourneyId('new_journey') };
      setNewJourneyConfig(j);
    }
  }, [selectedTemplate, inputType]);

  const handleAttributeSelection = useCallback((config: any) => {
    setNewJourneyConfig(prev => prev ? {
      ...prev,
      journey_workflow_config: {
        ...prev.journey_workflow_config,
        workflow_configuration: {
          ...prev.journey_workflow_config.workflow_configuration,
          analytics_configuration: {
            ...(prev.journey_workflow_config.workflow_configuration as any)?.analytics_configuration,
            analytics_attributes_configuration: config,
          },
        },
      },
    } : prev);
  }, []);

  const startEditing = (index: number) => {
    setEditingJourney(index);
    setEditAttrConfig(null);
    setExpandedJourney(index);
  };

  const handleSaveEdit = async (index: number) => {
    // Merge attribute config into existing workflow config
    const existing = existingJourneys[index].journey_workflow_config.workflow_configuration;
    const newConfig = {
      ...existing,
      analytics_configuration: {
        ...(existing as any)?.analytics_configuration,
        analytics_attributes_configuration: editAttrConfig,
        analytics_configuration_type: (existing as any)?.analytics_configuration?.analytics_configuration_type || 'V2',
      },
    };

    setIsSavingEdit(true);
    try {
      const updatedJourneys = existingJourneys.map((j, i) => {
        if (i !== index) return { journey_id: j.journey_id, journey_name: j.journey_name, journey_sub_text: j.journey_sub_text, journey_type: j.journey_type, journey_workflow_config: j.journey_workflow_config };
        return {
          journey_id: j.journey_id, journey_name: j.journey_name, journey_sub_text: j.journey_sub_text, journey_type: j.journey_type,
          journey_workflow_config: { ...j.journey_workflow_config, workflow_configuration: newConfig },
        };
      });

      await analyticsOnboardingAPI.configureAnalytics({
        gateway_instance_id: clientConfig.exchange_application_id!,
        business_id: clientConfig.analytics_config?.analytics_provider_config?.client_id?.split('#')[0] || '',
        client_id: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
        configured_journeys: updatedJourneys,
        header_name: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
      });

      setExistingJourneys(updatedJourneys as any);
      setEditingJourney(null);
      setSuccessMsg('Journey updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) { onError(err, { component: 'ManageJourneysView', action: 'editJourney' }); }
    finally { setIsSavingEdit(false); }
  };

  const handleDeleteJourney = async (index: number) => {
    setIsDeleting(true);
    try {
      const remaining = existingJourneys.filter((_, i) => i !== index).map(j => ({
        journey_id: j.journey_id, journey_name: j.journey_name, journey_sub_text: j.journey_sub_text,
        journey_type: j.journey_type, journey_workflow_config: j.journey_workflow_config,
      }));

      await analyticsOnboardingAPI.configureAnalytics({
        gateway_instance_id: clientConfig.exchange_application_id!,
        business_id: clientConfig.analytics_config?.analytics_provider_config?.client_id?.split('#')[0] || '',
        client_id: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
        configured_journeys: remaining,
        header_name: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
      });

      setExistingJourneys(remaining as any);
      setDeleteConfirmIdx(null);
      setExpandedJourney(null);
      setSuccessMsg('Journey deleted successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) { onError(err, { component: 'ManageJourneysView', action: 'deleteJourney' }); }
    finally { setIsDeleting(false); }
  };

  const handleEnableJourney = async (journeyId: string, type: ActiveType) => {
    const key = `${journeyId}:${type}`;
    setEnablingKey(key);
    try {
      await analyticsOnboardingAPI.enableJourney(journeyId, type);
      setActiveJourneys(prev => ({ ...prev, [type]: journeyId }));
      setSuccessMsg(`${journeyId} is now active for ${TYPE_LABEL[type]}`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) { onError(err, { component: 'ManageJourneysView', action: 'enableJourney' }); }
    finally { setEnablingKey(null); }
  };

  const handleAddJourney = async () => {
    if (!newJourneyId.trim() || !clientConfig.exchange_application_id || !newJourneyConfig) return;

    try {
      setIsAdding(true);
      const journeyToAdd: ConfiguredJourney = {
        ...newJourneyConfig,
        journey_id: newJourneyId.trim(),
      };

      // Re-configure with all existing journeys + new one
      const allJourneys = [...existingJourneys.map(j => ({
        journey_id: j.journey_id, journey_name: j.journey_name,
        journey_sub_text: j.journey_sub_text, journey_type: j.journey_type,
        journey_workflow_config: j.journey_workflow_config,
      })), journeyToAdd];

      await analyticsOnboardingAPI.configureAnalytics({
        gateway_instance_id: clientConfig.exchange_application_id,
        business_id: clientConfig.analytics_config?.analytics_provider_config?.client_id?.split('#')[0] || '',
        client_id: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
        configured_journeys: allJourneys,
        header_name: clientConfig.analytics_config?.analytics_provider_config?.client_id || '',
      });

      // Refresh
      const data = await analyticsOnboardingAPI.getConfiguredJourneys(clientConfig.exchange_application_id);
      setExistingJourneys(data?.configured_journeys || []);
      setShowAddJourney(false);
      setNewJourneyId('');
      setCopyFromJourney('');
      setSuccessMsg('Journey added successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      onError(err, { component: 'ManageJourneysView', action: 'addJourney' });
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 0 60px' }}>
      {/* Back */}
      <button onClick={onBack} style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13,
        color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer',
        padding: '4px 0', marginBottom: 28,
      }}>
        <ChevronLeft style={{ width: 14, height: 14 }} /> Back to Analytics
      </button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <div style={{ fontSize: 24, color: '#0f172a' }}><b>Analytics Configuration</b></div>
        </div>
        <div className="equal-ds-root" style={{ display: 'flex', gap: 10 }}>
          <Button variant="secondary" size="md" onClick={onTest}>
            <FlaskConical style={{ width: 16, height: 16 }} /> Test
          </Button>
          <Button variant="primary" size="md" onClick={() => setShowAddJourney(!showAddJourney)}>
            <Plus style={{ width: 16, height: 16 }} /> Add Journey
          </Button>
        </div>
      </div>

      {successMsg && (
        <div style={{
          padding: '12px 16px', borderRadius: 10, backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0',
          fontSize: 13, color: '#166534', marginBottom: 20,
        }}>
          {successMsg}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid #e2e8f0', borderTopColor: '#3b82f6', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          <div style={{ fontSize: 13 }}>Loading configured journeys...</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      ) : (
        <>
          {/* Existing Journeys */}
          {visibleJourneys.length === 0 && !showAddJourney ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8' }}>
              <Settings style={{ width: 40, height: 40, opacity: 0.3, margin: '0 auto 12px' }} />
              <div style={{ fontSize: 14 }}>No journeys configured yet.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: showAddJourney ? 32 : 0 }}>
              {visibleJourneys.map((journey, index) => {
                const isOpen = expandedJourney === index;
                const configType = getJourneyConfigType(journey);
                const isActive = activeJourneys[configType] === journey.journey_id;
                const enablingThis = enablingKey === `${journey.journey_id}:${configType}`;

                return (
                  <div key={journey.journey_id} style={{
                    borderRadius: 12, overflow: 'hidden',
                    border: '1px solid',
                    borderColor: isActive ? '#bbf7d0' : isOpen ? '#bfdbfe' : '#e8ecf0',
                    borderLeft: `3px solid ${isActive ? '#16a34a' : isOpen ? '#3b82f6' : '#e8ecf0'}`,
                    backgroundColor: isActive ? '#f0fdf4' : '#fff',
                    boxShadow: isActive
                      ? '0 2px 8px rgba(22,163,74,0.08)'
                      : isOpen ? '0 2px 8px rgba(37,99,235,0.06)' : '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.18s',
                  }}>
                    <div
                      onClick={() => setExpandedJourney(isOpen ? null : index)}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer' }}
                    >
                      {/* Left: icon + name + type label */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: isActive ? '#dcfce7' : isOpen ? '#dbeafe' : '#f1f5f9' }}>
                          <Settings style={{ width: 16, height: 16, color: isActive ? '#16a34a' : isOpen ? '#2563eb' : '#94a3b8' }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{journey.journey_id}</span>
                            {isActive && (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 700, color: '#15803d', backgroundColor: '#dcfce7', border: '1px solid #86efac', borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap', letterSpacing: '0.01em' }}>
                                ✓ Currently Active
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 12, color: isActive ? '#16a34a' : '#94a3b8', marginTop: 2, fontWeight: 500 }}>
                            {TYPE_LABEL[configType]}
                          </div>
                        </div>
                      </div>

                      {/* Right: Make Active + delete + chevron */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 12 }}>
                        {!isActive && (
                          <button
                            onClick={e => { e.stopPropagation(); handleEnableJourney(journey.journey_id, configType); }}
                            disabled={!!enablingKey}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: enablingThis ? '#15803d' : '#16a34a', backgroundColor: enablingThis ? '#f0fdf4' : '#fff', border: `1.5px solid ${enablingThis ? '#86efac' : '#bbf7d0'}`, borderRadius: 8, cursor: enablingKey ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(22,163,74,0.1)', transition: 'all 0.15s', opacity: enablingKey && !enablingThis ? 0.45 : 1 }}
                            onMouseEnter={e => { if (!enablingKey) { e.currentTarget.style.backgroundColor = '#f0fdf4'; e.currentTarget.style.borderColor = '#4ade80'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(22,163,74,0.18)'; } }}
                            onMouseLeave={e => { if (!enablingThis) { e.currentTarget.style.backgroundColor = '#fff'; e.currentTarget.style.borderColor = '#bbf7d0'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(22,163,74,0.1)'; } }}
                          >
                            {enablingThis
                              ? <><Loader2 style={{ width: 11, height: 11, animation: 'spin 1s linear infinite' }} /> Activating...</>
                              : 'Make Active'}
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); setDeleteConfirmIdx(index); }}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#cbd5e1', transition: 'all 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#cbd5e1'; }}
                        >
                          <Trash2 style={{ width: 14, height: 14 }} />
                        </button>
                        <ChevronDown style={{ width: 16, height: 16, color: '#94a3b8', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0 }} />
                      </div>
                    </div>

                    {isOpen && (
                      <div style={{ borderTop: '1px solid #f1f5f9', padding: '20px 22px' }}>
                        <div style={{ marginBottom: 16 }}>
                          {/* Edit/Explore buttons */}
                          <div className="equal-ds-root" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            {editingJourney === index ? (
                              <>
                                <Button variant="secondary" size="sm" onClick={() => setEditingJourney(null)}>Cancel</Button>
                                <Button variant="primary" size="sm" onClick={() => handleSaveEdit(index)} disabled={isSavingEdit}>
                                  {isSavingEdit ? <><Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> Saving...</> : <><Save style={{ width: 12, height: 12 }} /> Save</>}
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button variant="secondary" size="sm" onClick={() => startEditing(index)}>
                                  <Pencil style={{ width: 12, height: 12 }} /> Edit
                                </Button>
                                {onExploreAttributes && (
                                  <Button variant="primary" size="sm" onClick={() => onExploreAttributes(journey.journey_id)}>
                                    <BookOpen style={{ width: 12, height: 12 }} /> Understand Attributes
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {editingJourney === index ? (
                          <div>
                            <AttributeSelector
                              onSelectionChange={config => setEditAttrConfig(config)}
                              clientName={clientConfig.analytics_config?.analytics_provider_config?.client_id?.split('#')[0] || ''}
                              instanceId={clientConfig.exchange_application_id || ''}
                              initialConfig={(journey.journey_workflow_config?.workflow_configuration as any)?.analytics_configuration?.analytics_attributes_configuration}
                            />
                          </div>
                        ) : (
                          <JourneyConfigView config={journey.journey_workflow_config.workflow_configuration} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add New Journey Panel */}
          {showAddJourney && (
            <div style={{
              border: '2px solid #bfdbfe', borderRadius: 16, overflow: 'hidden', backgroundColor: '#fff',
              boxShadow: '0 4px 16px rgba(37,99,235,0.08)',
            }}>
              <div style={{ padding: '20px 24px', backgroundColor: '#eff6ff', borderBottom: '1px solid #dbeafe' }}>
                <div style={{ fontSize: 16, color: '#0f172a' }}><b>Add New Journey</b></div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>Configure a new analytics journey for your instance</div>
              </div>

              <div style={{ padding: '24px' }}>
                {/* Input Type — Consent or PDF */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Input Type</b></div>
                  <div className="equal-ds-root">
                    <TabSwitcher
                      tabs={[{ id: 'consent', label: 'Using Consent ID' }, { id: 'pdf', label: 'Using PDF' }]}
                      activeTab={inputType}
                      onTabChange={(t: string) => setInputType(t as 'consent' | 'pdf')}
                      variant="horizontal"
                    />
                  </div>
                </div>

                {/* Template */}
                <div style={{ marginBottom: 22 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Configuration Type</b></div>
                  <div className="equal-ds-root">
                    <TabSwitcher
                      tabs={TEMPLATE_TABS}
                      activeTab={selectedTemplate}
                      onTabChange={(t: string) => { setSelectedTemplate(t); setCopyFromJourney(''); }}
                      variant="horizontal"
                    />
                  </div>
                </div>

                {/* Copy from existing (PDF only) */}
                {inputType === 'pdf' && consentJourneys.length > 0 && (
                  <div style={{ marginBottom: 22 }}>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}><b>Or Copy Configuration From Existing Journey</b></div>
                    <select
                      value={copyFromJourney}
                      onChange={e => {
                        setCopyFromJourney(e.target.value);
                        const src = existingJourneys.find(j => j.journey_id === e.target.value);
                        if (src) {
                          setNewJourneyConfig({
                            journey_id: newJourneyId || toJourneyId('pdf_journey'),
                            journey_name: 'BANK_STATEMENT_OCR',
                            journey_sub_text: 'Verify Any Bank Statement',
                            journey_type: 'BANK_STATEMENT_OCR',
                            journey_workflow_config: {
                              workflow_configuration: src.journey_workflow_config.workflow_configuration,
                              workflow_id: WORKFLOW_IDS.PDF,
                            },
                          });
                        }
                      }}
                      id="copy-journey-select"
                    >
                      <option value="">Select a journey to copy from...</option>
                      {consentJourneys.map(j => (
                        <option key={j.journey_id} value={j.journey_id}>{j.journey_id}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Journey ID */}
                <div className="equal-ds-root">
                  <InputField
                    label="Journey ID"
                    value={newJourneyId}
                    onChange={e => { setNewJourneyId(e.target.value); if (newJourneyConfig) setNewJourneyConfig({ ...newJourneyConfig, journey_id: e.target.value }); }}
                    placeholder={inputType === 'pdf' ? 'e.g., pdf_analytics_v1' : 'e.g., my_custom_analytics'}
                    required
                  />
                </div>

                {selectedTemplate === 'DYNAMIC_ATTRIBUTES' && !copyFromJourney && (
                  <>
                    <div style={{ height: 24 }} />
                    <AttributeSelector onSelectionChange={handleAttributeSelection} clientName={clientConfig.analytics_config?.analytics_provider_config?.client_id?.split('#')[0] || ''} instanceId={clientConfig.exchange_application_id || ''} />
                  </>
                )}

              </div>

              {/* Footer */}
              <div className="equal-ds-root" style={{
                display: 'flex', justifyContent: 'flex-end', gap: 12,
                padding: '16px 24px', borderTop: '1px solid #f1f5f9',
              }}>
                <Button variant="secondary" size="md" onClick={() => setShowAddJourney(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="md" onClick={handleAddJourney} disabled={!newJourneyId.trim() || isAdding}>
                  {isAdding ? <><Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> Adding...</> : <>Add Journey <ArrowRight style={{ width: 16, height: 16 }} /></>}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        #copy-journey-select,
        .journey-select {
          width: 100% !important;
          padding: 10px 40px 10px 14px !important;
          font-size: 14px !important;
          color: #0f172a !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          background-color: #fff !important;
          outline: none !important;
          height: 44px !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
          -webkit-appearance: none !important;
          -moz-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 12px center !important;
          background-size: 16px !important;
          cursor: pointer !important;
          transition: border-color 0.15s !important;
        }
        #copy-journey-select:focus,
        .journey-select:focus {
          border-color: #93c5fd !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important;
        }
      `}</style>


      {/* Delete confirmation dialog */}
      {deleteConfirmIdx !== null && (
        <>
          <div onClick={() => setDeleteConfirmIdx(null)} style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.2)', zIndex: 9998,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 420, backgroundColor: '#fff', borderRadius: 16, padding: '28px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)', zIndex: 9999,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Trash2 style={{ width: 20, height: 20, color: '#ef4444' }} />
              </div>
              <div>
                <div style={{ fontSize: 16, color: '#0f172a' }}><b>Delete Journey</b></div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>This action cannot be undone</div>
              </div>
            </div>
            <div style={{ fontSize: 14, color: '#334155', marginBottom: 8, lineHeight: 1.5 }}>
              Are you sure you want to delete <b>{existingJourneys[deleteConfirmIdx]?.journey_id}</b>? This will remove the journey and its configuration permanently.
            </div>

            <div style={{ height: 24 }} />

            <div className="equal-ds-root" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button variant="primary" size="md" onClick={() => setDeleteConfirmIdx(null)} disabled={isDeleting}>
                Cancel
              </Button>
              <div id="delete-journey-btn-wrap">
                <Button variant="primary" size="md" onClick={() => handleDeleteJourney(deleteConfirmIdx)} disabled={isDeleting}>
                  {isDeleting ? 'Deleting...' : 'Delete Journey'}
                </Button>
              </div>
              <style>{`
                #delete-journey-btn-wrap button,
                #delete-journey-btn-wrap button:hover {
                  background-color: #ef4444 !important;
                  border-color: #dc2626 !important;
                }
              `}</style>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
